import { useTranslation } from "react-i18next";
import DashboardStats from "../../components/Stats/DashboardStats";
import ReactECharts from "echarts-for-react";
import services from "../../services";
import { useQuery } from "@tanstack/react-query";
import BarLoading from "../../components/Loading/BarLoading";
import { amountFormatter, currencyFormatter } from "../../utils/helper";
import { useState, useEffect } from "react";
import { compose, withProps, withStateHandlers } from "recompose";
import {
  GoogleMap,
  Polygon,
  withGoogleMap,
  withScriptjs,
  Marker,
  InfoWindow,
} from "react-google-maps";
import { Tabs } from "@mantine/core";
import BasaksehirTransactions from "../../components/BasaksehirTransaction/BasaksehirTransaction";
import CustomTabs from "../../components/CustomTabs";
import dayjs from "dayjs";
import SpotsCustomList from "../../components/SpotsCustomList/SpostsCustomList";
import CustomSelect from "../../components/CustomSelect/CustomSelect";
import { useNavigate } from "react-router-dom";
import Financial from "../../components/Financial/Financial";
import { useStore } from "../../hooks";

const defaultLocation = { lat: 41.095442509457285, lng: 28.802870489816268 };
const mapStyles = require("../../static/mapStyles.json");

const iconReturner = (type) => {
  switch (type) {
    case "CRITICAL":
      return require("../../assets/ChevronThreeUp.svg").default;
    case "NORMAL":
      return require("../../assets/ChevronTwoUp.svg").default;
    case "FULL":
      return require("../../assets/ChevronOneUp.svg").default;
    default:
      return require("../../assets/ChevronOneUp.svg").default;
  }
};

const MapComponent = compose(
  withStateHandlers(
    () => ({
      isOpen: false,
      openId: 0,
    }),
    {
      onToggleOpen:
        ({ isOpen }) =>
        (id) => ({
          isOpen: !isOpen,
          openId: id || 0,
        }),
    }
  ),
  withProps({
    googleMapURL:
      "https://maps.googleapis.com/maps/api/js?v=3.exp&libraries=geometry,drawing&key=AIzaSyCBM4uTkUhiTpZYBUFxR5NZ2ayD6S-PrmI",
    loadingElement: <div className="w-full" style={{ height: `100%` }} />,
    containerElement: <div className="w-full" style={{ height: `100%` }} />,
    mapElement: <div className="w-full" style={{ height: `100%` }} />,
  }),
  withScriptjs,
  withGoogleMap
)(({ center, zoom = 12, data, isOpen, openId, onToggleOpen }) => {
  const { t } = useTranslation();
  return (
    <GoogleMap
      defaultZoom={zoom}
      zoom={zoom}
      center={center || defaultLocation}
      defaultCenter={defaultLocation}
      defaultOptions={mapStyles}
    >
      {data?.main_sale_areas?.map((area, index) => (
        <Polygon
          key={`main-${index}`}
          options={{
            fillColor: "rgba(0,0,0,0.4)",
            editable: false,
          }}
          paths={area.main_sale_area_coordinates}
        />
      ))}

      {data?.sale_areas?.map((area, index) => (
        <Polygon
          key={`sale-area-${index}`}
          options={{
            fillColor: "rgba(217, 119, 6, 0.7)",
            editable: false,
          }}
          paths={area.sale_area_coordinates}
        />
      ))}

      {data?.sale_spots?.map((spot, index) => (
        <Marker
          key={`spot-${index}`}
          position={{
            lat: parseFloat(spot.sale_area_coordinates[0].lat),
            lng: parseFloat(spot.sale_area_coordinates[0].lng),
          }}
          icon={{
            url: iconReturner(spot.stock_status.status),
            scaledSize: new window.google.maps.Size(65, 65),
          }}
          onClick={() => {
            onToggleOpen(index);
          }}
        >
          {isOpen && openId === index && (
            <InfoWindow onCloseClick={onToggleOpen}>
              <div className="text-black">
                <div className="font-semibold">{spot.sale_spot_name}</div>
                <div className="text-sm text-gray-600">
                  {spot.sale_area_name}
                </div>
                <div className="text-sm">
                  {t("dashboard.status")}: {spot.sale_spot_status}
                </div>
                <div className="text-sm">
                  {t("dashboard.last-updated")}:{" "}
                  {dayjs(spot.sale_spot_status_updated_at).fromNow()}
                </div>
                <div className="flex flex-row gap-2 items-center">
                  <div className="text-md">
                    {t("dashboard.stock-status-percentage")}:
                  </div>
                  <div className="text-sm">{spot.stock_status.percentage}%</div>
                </div>
                <div className="flex flex-row gap-2 items-center">
                  <div className="text-md">
                    {t("dashboard.stock-status-total-current-stock")}:
                  </div>
                  <div className="text-sm">
                    {spot.stock_status.total_current_stock} /{" "}
                    {spot.stock_status.total_capacity}
                  </div>
                </div>
              </div>
            </InfoWindow>
          )}
        </Marker>
      ))}
    </GoogleMap>
  );
});

const BasaksehirManagement = () => {
  const { t } = useTranslation();
  const [stats, setStats] = useState([]);
  const [options, setOptions] = useState({});
  const [activeTab, setActiveTab] = useState("spots");
  const [saleSpotId, setSaleSpotId] = useState("all");
  const navigate = useNavigate();

  const { auth } = useStore();
  const userRoles = auth?.user?.roles || [];

  const hasFinanceAccess = userRoles.some(
    (role) => role.includes("finance") || role.includes("it")
  );
  const hasSalesAccess = userRoles.some(
    (role) => role.includes("sales") || role.includes("it")
  );
  const hasITAccess = userRoles.some((role) => role.includes("it"));

  useEffect(() => {
    if (hasITAccess || hasSalesAccess) {
      setActiveTab("spots");
    } else if (hasFinanceAccess && !hasSalesAccess) {
      setActiveTab("financial");
    }
  }, [hasITAccess, hasFinanceAccess, hasSalesAccess]);

  const {
    isLoading: customLoading,
    error: customError,
    data: customData,
  } = useQuery({
    queryKey: ["custom-data"],
    queryFn: async () => await services.getCustomDashboardData(),
    cacheTime: 0,
    staleTime: 0,
    enabled: hasITAccess || hasSalesAccess,
  });

  useEffect(() => {
    navigate("/", { replace: true });
  }, [navigate]);

  useEffect(() => {
    if (customData?.data) {
      const { stats: statsData, product_sale_report } = customData.data;

      let formattedStats = [];

      if (hasSalesAccess) {
        formattedStats.push(
          {
            id: 1,
            name: t("dashboard.unique-user-count"),
            value: statsData?.unique_user_count || 0,
          },
          {
            id: 2,
            name: t("dashboard.total-transaction-count"),
            value: statsData?.total_transaction_count || 0,
          },
          {
            id: 3,
            name: t("dashboard.total-product-count"),
            value: statsData?.total_product_count || 0,
          },
          {
            id: 4,
            name: t("dashboard.success-percentage"),
            value: statsData?.success_percentage || 0,
          }
        );
      }

      if (hasFinanceAccess) {
        formattedStats.push({
          id: 5,
          name: t("dashboard.total-sales-amount"),
          value: currencyFormatter(statsData?.total_price || 0),
        });
      }

      setStats(formattedStats);

      if (product_sale_report && product_sale_report.length > 0) {
        setOptions({
          xAxis: {
            type: "category",
            data: product_sale_report.map((item) => item.label),
            axisLabel: {
              interval: 0,
              rotate: 30,
            },
          },
          yAxis: {
            type: "value",
            splitLine: {
              lineStyle: {
                color: "rgba(30,68,255,0.2)",
              },
            },
          },
          tooltip: {
            trigger: "axis",
            axisPointer: {
              type: "shadow",
            },
            formatter: function (params) {
              const item = product_sale_report[params[0].dataIndex];
              return (
                item.label +
                "<br/>" +
                params[0].marker +
                " " +
                t("labels.transaction-count") +
                ": " +
                item.transaction_count +
                "<br/>" +
                params[0].marker +
                " " +
                t("labels.product-count") +
                ": " +
                item.product_count +
                "<br/>" +
                params[0].marker +
                " " +
                t("labels.customer-count") +
                ": " +
                item.total_customer_count +
                "<br/>" +
                params[0].marker +
                " " +
                t("labels.amount-mny") +
                ": " +
                amountFormatter(item.total_price) +
                " TL"
              );
            },
          },
          grid: {
            left: "5%",
            right: "5%",
            bottom: "15%",
            containLabel: true,
          },
          series: [
            {
              data: product_sale_report.map((item) => item.total_price),
              name: t("labels.transaction-count"),
              type: "bar",
              showBackground: true,
              backgroundStyle: {
                color: "rgba(0,0,0,0.08)",
              },
              emphasis: {
                itemStyle: {
                  borderRadius: [2, 2, 0, 0],
                  color: "#1E44FF",
                },
              },
              itemStyle: {
                borderRadius: [2, 2, 0, 0],
                color: "#1E44FF",
              },
            },
          ],
        });
      }
    }
  }, [customData, t, hasFinanceAccess, hasSalesAccess]);

  const renderChart = () => {
    return (
      <div
        className="h-full w-full "
        style={{
          minHeight: "70vh",
        }}
      >
        {customLoading && (hasITAccess || hasSalesAccess) && (
          <div className="absolute top-0 left-0 w-full h-full bg-gray-400/10">
            <BarLoading />
          </div>
        )}
        {!customLoading && (
          <ReactECharts
            className="md:!h-full md:w-[98%] mx-auto"
            option={options}
            notMerge={true}
            lazyUpdate={true}
            style={{ height: "100%", width: "100%" }}
          />
        )}
      </div>
    );
  };

  const renderContent = () => {
    switch (activeTab) {
      case "transactions":
        return <BasaksehirTransactions saleSpotId={saleSpotId} />;

      case "financial":
        return <Financial />;

      case "chart":
        return (
          <div className="bg-gray-400/10 py-8 rounded-2xl w-full h-[70vh]  relative block">
            {renderChart()}
          </div>
        );
      case "spots":
        return (
          <div className="grid grid-cols-1 w-full gap-4 min-h-[calc(100vh-400px)]">
            <div className="bg-gray-400/10 rounded-2xl w-full h-[500px] md:h-full relative block">
              <MapComponent data={customData?.data} />
            </div>
          </div>
        );
      case "spots-list":
        return <SpotsCustomList data={customData?.data} />;
      default:
        return null;
    }
  };

  return (
    <div className="flex flex-col h-screen overflow-scroll">
      {customLoading && (hasITAccess || hasSalesAccess) ? (
        <div className="bg-gray-400/10 h-full w-full">
          <BarLoading />
        </div>
      ) : (
        <div className="flex flex-col h-full justify-between">
          <div>
            {/* Show stats only for roles that have access to sales data or IT */}
            {(hasSalesAccess || hasITAccess) && <DashboardStats data={stats} />}
            <div className="mt-4 px-4 mb-4 flex  flex-col-reverse md:flex-row gap-2 md:gap-0 md:justify-end ">
              {activeTab === "transactions" && (
                <div className="flex flex-row gap-4 mr-auto mt-2 w-full md:w-auto justify-center md:justify-end">
                  <CustomSelect
                    withoutController={true}
                    data={[
                      {
                        label: "Tümü",
                        value: "all",
                      },
                      ...(customData?.data?.sale_spots?.map((spot) => ({
                        label: spot.sale_spot_location_description,
                        value: spot.sale_spot_id,
                      })) || []),
                    ]}
                    className="w-full md:w-auto"
                    value={saleSpotId}
                    name="saleSpotId"
                    placeholder={t("actions.select")}
                    onChange={(value) => {
                      setSaleSpotId(value);
                      navigate("/", { replace: true });
                    }}
                  />
                </div>
              )}

              {(hasITAccess || hasSalesAccess) && (
                <CustomTabs
                  activeTab={activeTab}
                  onChange={(value) => {
                    setActiveTab(value);
                    if (value !== "transactions") {
                      navigate("/", { replace: true });
                    }
                  }}
                  tabs={[
                    ...(hasITAccess || hasSalesAccess
                      ? [
                          {
                            label: t("dashboard.sale-chart"),
                            value: "chart",
                          },
                        ]
                      : []),
                    ...(hasITAccess || hasSalesAccess
                      ? [
                          {
                            label: t("dashboard.spots"),
                            value: "spots",
                          },
                          {
                            label: t("dashboard.spots-list"),
                            value: "spots-list",
                          },
                        ]
                      : []),

                    ...(hasSalesAccess || hasITAccess
                      ? [
                          {
                            label: t("dashboard.transactions"),
                            value: "transactions",
                          },
                        ]
                      : []),
                    ...(hasFinanceAccess || hasSalesAccess || hasITAccess
                      ? [
                          {
                            label: t("labels.financial"),
                            value: "financial",
                          },
                        ]
                      : []),
                  ]}
                >
                  <Tabs.List>
                    <Tabs.Tab value="spots">{t("labels.spots")}</Tabs.Tab>
                    <Tabs.Tab value="transactions">
                      {t("labels.transactions")}
                    </Tabs.Tab>
                  </Tabs.List>
                </CustomTabs>
              )}
            </div>
          </div>
          <div className="flex gap-4 flex-col lg:flex-nowrap place-items-center h-full pb-4">
            {renderContent()}
          </div>
        </div>
      )}
    </div>
  );
};

export default BasaksehirManagement;
